import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter.js";

dayjs.extend(isSameOrAfter);

/** sanctuary_room 없음 = 기타 성전 신청 */
export const formatOtherSanctuaryLabel = (rawName) => {
	const name = String(rawName ?? "").trim();
	return name ? `기타 - ${name}` : "-";
};

/** 일일 목록·캘린더 Tag 라벨 (기타 성전은 sanctuary_room 없음) */
export const formatSanctuaryRoomTag = (row) => {
	const sr = row.sanctuary_room;
	if (!sr) {
		return formatOtherSanctuaryLabel(
			row.custom_sanctuary_name || row.sanctuary_name,
		);
	}
	if (sr.room_name === "-") {
		return sr.sanctuary_name;
	}
	return `${sr.sanctuary_name} ${sr.room_name}`;
};

export const SANCTUARY_SCHEDULE_TAG_COLORS = [
	"cyan",
	"blue",
	"geekblue",
	"purple",
	"magenta",
	"volcano",
	"gold",
	"lime",
	"green",
];

export const scheduleRowSanctuaryName = (row) =>
	row?.sanctuary_room?.sanctuary_name ?? row?.sanctuary_name ?? null;

export const sanctuaryScheduleColorKey = (row) => {
	const name = scheduleRowSanctuaryName(row);
	if (name != null && String(name).trim() !== "") {
		return String(name).trim();
	}
	return (
		String(
			formatOtherSanctuaryLabel(
				row.custom_sanctuary_name || row.sanctuary_name,
			),
		).trim() || "-"
	);
};

export const sanctuaryScheduleTagColor = (row) => {
	const key = sanctuaryScheduleColorKey(row);
	let hash = 0;
	for (let i = 0; i < key.length; i++) {
		hash = (hash << 5) - hash + key.charCodeAt(i);
		hash |= 0;
	}
	return SANCTUARY_SCHEDULE_TAG_COLORS[
		Math.abs(hash) % SANCTUARY_SCHEDULE_TAG_COLORS.length
	];
};

export const truncateCellTagText = (text, maxLen = 4) => {
	const s = text == null ? "" : String(text).replace("성전", "");
	if (s.length <= maxLen) {
		return s;
	}
	return s.slice(0, maxLen);
};

export const getScheduleSanctuaryLabel = (schedule) => {
	if (!schedule?.sanctuary_room) {
		return formatOtherSanctuaryLabel(
			schedule.custom_sanctuary_name ||
				scheduleRowSanctuaryName(schedule),
		);
	}
	const sr = schedule.sanctuary_room;
	if (sr.room_name === "-") {
		return sr.sanctuary_name;
	}
	return `${sr.sanctuary_name}`;
};

export const getApplicationSanctuaryLabel = (application) => {
	if (!application?.sanctuary_room) {
		return formatOtherSanctuaryLabel(
			application.custom_sanctuary_name ||
				application.sanctuary_name,
		);
	}
	if (application.sanctuary_room?.room_name === "-") {
		return (
			application.sanctuary_room?.sanctuary_name ||
			application.sanctuary_name ||
			"-"
		);
	}
	return (
		application.sanctuary_name ||
		application.sanctuary_room?.sanctuary_name
	);
};

export const getUniqueSanctuaryNames = (sanctuaryRoomList) => {
	const names = [
		...new Set(
			sanctuaryRoomList
				.map((r) => r.sanctuary_name)
				.filter(Boolean),
		),
	];
	return names.sort((a, b) => a.localeCompare(b, "ko"));
};

export const calculateLastSundayOfTheMonth = (date) => {
	let lastDayOfMonth = dayjs(date).endOf("month");
	let dayOfWeek = lastDayOfMonth.day();
	let daysToSubtract = dayOfWeek === 0 ? 0 : dayOfWeek;
	return lastDayOfMonth.subtract(daysToSubtract, "day");
};

export const checkIfTodayIsBeforeLastSundayOfTheMonth = () => {
	return dayjs().isBefore(
		calculateLastSundayOfTheMonth(dayjs()).subtract(1, "day"),
	);
};

export const checkIfDateIsInNextMonth = (date) => {
	return date.isSame(dayjs().add(1, "month"), "month");
};

export const isDateRentableForNonOffice = (date) => {
	if (!date) return false;
	if (date.isBefore(dayjs().subtract(1, "day"))) {
		return false;
	}
	if (
		date.isSameOrAfter(dayjs().add(2, "month"), "month") ||
		(checkIfTodayIsBeforeLastSundayOfTheMonth() &&
			checkIfDateIsInNextMonth(date))
	) {
		return false;
	}
	return true;
};

export const isDateDisabledForNonOffice = (current) => {
	return current && !isDateRentableForNonOffice(current);
};

/** 00:00 ~ 23:30, 30분 간격 48개 슬롯 */
export const generateTimeSlots = () => {
	const slots = [];
	for (let h = 0; h < 24; h++) {
		for (const m of [0, 30]) {
			slots.push({ hour: h, minute: m });
		}
	}
	return slots;
};

export const formatSlotLabel = (slot) => {
	const h = slot.hour;
	const m = slot.minute;
	if (h === 0 && m === 0) return "0:00";
	if (m === 0) return `${h}:00`;
	return `${h}:${String(m).padStart(2, "0")}`;
};

export const slotToDayjs = (date, slot) =>
	dayjs(date).hour(slot.hour).minute(slot.minute).second(0).millisecond(0);

export const slotKey = (slot) => `${slot.hour}:${slot.minute}`;

export const parseSlotKey = (key) => {
	const [h, m] = key.split(":").map(Number);
	return { hour: h, minute: m };
};

export const isSlotOverlapping = (booking, slotStart, slotEnd) => {
	const bStart = dayjs(booking.start_datetime);
	const bEnd = dayjs(booking.end_datetime);
	return bStart.isBefore(slotEnd) && bEnd.isAfter(slotStart);
};

const getBookingsForRoom = (bookings, room) => {
	return bookings.filter((b) => {
		if (!b.sanctuary_room) return false;
		return b.sanctuary_room.id === room.id;
	});
};

const getAbrahamBookings = (bookings, sanctuaryName) => {
	return bookings.filter(
		(b) => b.sanctuary_room?.sanctuary_name === sanctuaryName,
	);
};

/** 단일 room이 선택된 모든 슬롯에 대해 가용한지 (아브라함성전 규칙 포함) */
export const isRoomAvailable = (room, selectedSlots, date, bookings) => {
	if (!selectedSlots.length) return false;

	const abrahamName = "아브라함성전";
	const isAbraham = room.sanctuary_name === abrahamName;
	const abrahamBookings = isAbraham
		? getAbrahamBookings(bookings, abrahamName)
		: [];

	for (const slot of selectedSlots) {
		const slotStart = slotToDayjs(date, slot);
		const slotEnd = slotStart.add(30, "minute");

		const roomBookings = getBookingsForRoom(bookings, room);
		for (const b of roomBookings) {
			if (isSlotOverlapping(b, slotStart, slotEnd)) {
				return false;
			}
		}

		if (isAbraham) {
			if (room.room_name === "전체") {
				const anyAbraham = abrahamBookings.some((b) =>
					isSlotOverlapping(b, slotStart, slotEnd),
				);
				if (anyAbraham) return false;
			} else {
				const wholeBooked = abrahamBookings.some(
					(b) =>
						b.sanctuary_room?.room_name === "전체" &&
						isSlotOverlapping(b, slotStart, slotEnd),
				);
				if (wholeBooked) return false;
			}
		}
	}
	return true;
};

export const getAvailableRooms = (rooms, selectedSlots, date, bookings) => {
	if (!selectedSlots.length) return [];
	return rooms.filter((room) =>
		isRoomAvailable(room, selectedSlots, date, bookings),
	);
};

/** 슬롯에 예약 가능한 room이 하나라도 있으면 활성 */
export const isSlotGloballyAvailable = (slot, date, rooms, bookings) => {
	return rooms.some((room) =>
		isRoomAvailable(room, [slot], date, bookings),
	);
};

/** 연속 슬롯인지 확인 */
export const areSlotsContiguous = (slotKeys) => {
	if (slotKeys.length <= 1) return true;
	const indices = slotKeys
		.map((key) => {
			const { hour, minute } = parseSlotKey(key);
			return hour * 2 + (minute === 30 ? 1 : 0);
		})
		.sort((a, b) => a - b);
	for (let i = 1; i < indices.length; i++) {
		if (indices[i] !== indices[i - 1] + 1) {
			return false;
		}
	}
	return true;
};

export const getRoomDisplayLabel = (room) => {
	if (room.room_name === "-") {
		return room.sanctuary_name;
	}
	return `${room.sanctuary_name} ${room.room_name}`;
};

/** 성전 대여 관리(승인·수정·등록) 권한 */
export const isOfficeSanctuaryUser = () => {
	try {
		const groupStr = localStorage.getItem("user_groups");
		const groups = groupStr ? JSON.parse(groupStr) : [];
		return Array.isArray(groups) && groups.includes("office_sanctuary");
	} catch {
		return false;
	}
};

export const filterByVisibleSanctuaries = (rows, visibleSanctuaryNames) => {
	if (!visibleSanctuaryNames || visibleSanctuaryNames.size === 0) {
		return [];
	}
	return rows.filter((row) => {
		const name = scheduleRowSanctuaryName(row);
		if (name) {
			return visibleSanctuaryNames.has(name);
		}
		return visibleSanctuaryNames.has(
			formatOtherSanctuaryLabel(
				row.custom_sanctuary_name || row.sanctuary_name,
			),
		);
	});
};
